import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { useGenericScriptWrapper } from "@/utils/scripts";
import { clearAllStorageByPattern } from "@/utils/cookieUtils";
import { TRACKING_VENDOR_STORAGE_PATTERNS } from "@/constants/trackingVendorStoragePatterns";

export const useFullStoryStore = defineStore("fullStory", () => {
  let _script: any = null;

  const getFullStoryScript = () => {
    if (import.meta.server) return null;
    if (!_script) {
      _script = useGenericScriptWrapper<any>(
        { src: "https://edge.fullstory.com/s/fs.js" },
        { trigger: "manual" as any },
      );
    }
    return _script;
  };

  const isBooted = ref(false);
  let isBooting = false;

  const isFullStoryAvailable = computed(() => {
    const script = getFullStoryScript();
    return script?.status.value === "loaded";
  });

  const bootFullStory = async () => {
    if (import.meta.server) return;
    if (isBooting || isBooted.value) return;
    isBooting = true;

    try {
      const userStore = useUserStore();
      const orgStore = useOrgStore();

      const user = userStore.getUser;
      const org = orgStore.getOrg;

      if (!user?.id || !org?.id || !org?.attributes) {
        // eslint-disable-next-line no-console
        console.log(
          "FullStory initialization skipped: Required user or org data not available",
        );
        return;
      }

      // Initialize FullStory command queue to collect commands before script loads
      if (!window.FS) {
        window._fs_host = "fullstory.com";
        window._fs_script = "edge.fullstory.com/s/fs.js";
        window._fs_org = "o-1W0XY0-na1";
        window._fs_namespace = "FS";

        const m: any = window;
        const e: string = window._fs_namespace;

        const g: any = (m[e] = function (a: any, b: any, s: any) {
          g.q ? g.q.push([a, b, s]) : g._api(a, b, s);
        });
        g.q = [];

        g.identify = function (i: any, v: any, s: any) {
          g("identify", { uid: i }, s);
          if (v) g("setUserVars", v, s);
        };
        g.setUserVars = function (v: any, s: any) {
          g("setUserVars", v, s);
        };
        g.event = function (i: any, v: any, s: any) {
          g("event", { n: i, p: v }, s);
        };
        g.shutdown = function () {
          g("rec", !1);
        };
        g.restart = function () {
          g("rec", !0);
        };
        g.log = function (a: any, b: any) {
          g("log", [a, b]);
        };
        g.consent = function (a: any) {
          g("consent", !arguments.length || a);
        };
      }

      const script = getFullStoryScript();
      if (!script) return;

      if (script.status.value !== "loaded") {
        await script.load();
      }

      await script.onLoaded(() => {});

      try {
        (window.FS as any)("identify", {
          uid: `360-${user.id}`,
          properties: {
            displayName: user.name,
            email: user.email,
            orgId: org.id,
            orgName: org.attributes.name,
            planName: org.attributes.plan_name,
            timezone_str: org.attributes.time_zone,
            isSuperAdmin_bool: user.role_name === "super_admin",
            userSignUpDate: user.created_at,
            orgSignUpDate: org.attributes.created_at,
            orgEndDate: org.attributes.end_date,
            trialAccount: org.attributes.trial_account,
            salesRegion: org.attributes.sales_region,
            appVersion: "1.0",
          },
        });
        isBooted.value = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to set FullStory identity:", error);
      }
    } finally {
      isBooting = false;
    }
  };

  const shutdownFullStory = () => {
    if (window.FS) {
      (window.FS as any)("rec", false);
    }
    isBooted.value = false;
    isBooting = false;
    clearAllStorageByPattern(TRACKING_VENDOR_STORAGE_PATTERNS.fullStory);
  };

  return {
    isFullStoryAvailable,
    isBooted: readonly(isBooted),
    bootFullStory,
    shutdownFullStory,
  };
});
